// MS-6 — "Tutto sotto controllo": presentational replica of the real dashboard Home.
// Static mock only: no data fetching, no new capability, no backend.
import { Briefcase, FileText, PlusCircle } from "lucide-react";

// Human-readable labels for the landing preview (same visual language as the app badge).
const BADGE_STYLES = {
  positive: "bg-teal-50 text-teal-700 ring-1 ring-inset ring-teal-600/20",
  pending: "bg-white text-neutral-700 ring-1 ring-inset ring-neutral-300",
};

const Badge = ({ label, tone = "positive" }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${BADGE_STYLES[tone]}`}
  >
    {label}
  </span>
);

const activeJobs = [
  { title: "Lavoro Mario Rossi a Via Roma 12" },
  { title: "Lavoro Bar Centrale a Piazza Verdi 3" },
];

const quotes = [
  { title: "Lavoro Mario Rossi a Via Roma 12", total: "€ 436,00", v: "v2" },
  { title: "Lavoro Fam. Bianchi a Via Alpi 8", total: "€ 1.240,00", v: "v1" },
];

const extras = [
  { desc: "Stuccatura muretto lato scala", total: "€ 80,00", label: "Confermato", tone: "positive", job: "Mario Rossi — Via Roma 12" },
  { desc: "Seconda mano di vernice", total: "€ 120,00", label: "Da confermare", tone: "pending", job: "Bar Centrale — Piazza Verdi 3" },
];

const MockSection = ({ title, count, children }) => (
  <div className="mt-6 first:mt-0">
    <div className="mb-2.5 flex items-center justify-between">
      <h4 className="text-sm font-bold text-neutral-950">{title}</h4>
      <span className="rounded-full bg-neutral-100 px-2.5 py-0.5 text-[11px] font-medium text-neutral-600">
        {count}
      </span>
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

const MockCard = ({ children }) => (
  <div className="rounded-xl border border-neutral-200 bg-white px-3.5 py-2.5 text-[13px]">
    {children}
  </div>
);

export const DashboardPreview = () => (
  <div
    data-testid="landing-dashboard-preview"
    className="grid grid-cols-1 items-center gap-14 lg:grid-cols-[0.78fr_1.22fr] lg:gap-16"
  >
    {/* Replica statica della Home di Mastro */}
    <div
      aria-hidden="true"
      className="order-2 w-full rounded-2xl border border-neutral-200/80 bg-white p-4 shadow-[0_4px_18px_-8px_rgba(15,23,42,0.10)] sm:p-5 lg:order-1 lg:max-w-[440px]"
    >
      <div className="mb-6">
        <div className="text-lg font-bold tracking-tight text-neutral-950">Panoramica</div>
      </div>

      <MockSection title="Lavori attivi" count={activeJobs.length}>
        {activeJobs.map((j) => (
          <MockCard key={j.title}>
            <div className="flex items-center justify-between gap-3">
              <span className="flex min-w-0 items-center gap-2 font-medium text-neutral-950">
                <Briefcase className="h-4 w-4 shrink-0 text-neutral-400" />
                <span className="truncate">{j.title}</span>
              </span>
              <Badge label="In corso" />
            </div>
          </MockCard>
        ))}
      </MockSection>

      <MockSection title="Preventivi da confermare" count={quotes.length}>
        {quotes.map((q) => (
          <MockCard key={q.title}>
            <div className="flex items-center justify-between gap-3">
              <span className="flex min-w-0 items-center gap-2 font-medium text-neutral-950">
                <FileText className="h-4 w-4 shrink-0 text-neutral-400" />
                <span className="truncate">{q.title}</span>
              </span>
              <span className="shrink-0 font-semibold text-neutral-950">{q.total}</span>
            </div>
            <div className="mt-1 text-[11px] text-neutral-500">Da confermare · {q.v}</div>
          </MockCard>
        ))}
      </MockSection>

      <MockSection title="Extra recenti" count={extras.length}>
        {extras.map((e) => (
          <MockCard key={e.desc}>
            <div className="flex items-center justify-between gap-3">
              <span className="flex min-w-0 items-center gap-2 text-neutral-950">
                <PlusCircle className="h-4 w-4 shrink-0 text-neutral-400" />
                <span className="truncate font-medium">{e.desc}</span>
              </span>
              <div className="flex shrink-0 items-center gap-2">
                <span className="text-neutral-600">{e.total}</span>
                <Badge label={e.label} tone={e.tone} />
              </div>
            </div>
            <div className="mt-1 truncate text-[11px] text-neutral-500">{e.job}</div>
          </MockCard>
        ))}
      </MockSection>
    </div>

    <div className="order-1 lg:order-2">
      <p className="text-sm font-bold uppercase tracking-[0.2em] text-teal-600 sm:text-base">
        Tutto sotto controllo
      </p>
      <h2 className="mt-5 max-w-xl text-3xl font-extrabold leading-[1.15] tracking-tight text-neutral-950 sm:text-4xl">
        Quando vuoi controllare il lavoro, sai dove guardare.
      </h2>
      <p className="mt-7 max-w-xl text-base leading-relaxed text-neutral-700 md:text-lg">
        Lavori, aggiornamenti, extra e preventivi restano raccolti in un unico posto.
        Quando vuoi vedere il quadro completo, apri Mastro.
      </p>
      <p className="mt-10 max-w-xl border-l-4 border-teal-500 pl-5 text-lg font-extrabold tracking-tight text-neutral-950 md:text-xl">
        WhatsApp per raccontare. Mastro per controllare.
      </p>
    </div>
  </div>
);
